import React, { useEffect, useState } from "react";
import { Code2 } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { CopyButton } from "./ui/copy-button";
import { OverflowDisplayTooltip } from "./ui/Tooltip/OverflowDisplayTooltip";

function formatCode(code: string): string {
  try {
    const parsed: unknown = JSON.parse(code);
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return Object.entries(parsed as Record<string, unknown>)
        .map(
          ([key, value]) => `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`,
        )
        .join("\n");
    }
  } catch {
    // Not JSON, return as-is
  }
  return code;
}

interface CodeAccordionProps {
  code: string;
  id: string | number;
  defaultOpen?: boolean;
  className?: string;
  containerId?: string;
  label?: string;
  icon?: React.ReactNode;
}

const CodeAccordion: React.FC<CodeAccordionProps> = ({
  code,
  id,
  defaultOpen = false,
  className = "",
  containerId,
  label = "Code",
  icon,
}) => {
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    defaultOpen ? `item-${id}` : undefined,
  );

  useEffect(() => {
    if (defaultOpen) {
      setAccordionValue(`item-${id}`);
    }
  }, [defaultOpen, id]);

  return (
    <div className={`mb-3 w-full rounded-lg border border-gray-200 bg-white ${className}`}>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value={`item-${id}`} className="border-0">
          <AccordionTrigger className="group overflow-hidden rounded-t-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50/80 hover:no-underline data-[state=open]:rounded-none data-[state=open]:border-b data-[state=open]:border-gray-200">
            <div className="flex items-center gap-2">
              {icon || <Code2 className="h-4 w-4 text-gray-500" />}
              <span className="text-sm font-medium">{label}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="group p-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="relative rounded-b-lg bg-gray-50/30 px-4 py-4">
              {containerId && (
                <div className="mb-3 border-b border-gray-200 pb-3">
                  <div className="font-mono text-xs text-gray-500">
                    Container ID:{" "}
                    <OverflowDisplayTooltip content={containerId}>
                      <span className="whitespace-nowrap text-gray-700">{containerId}</span>
                    </OverflowDisplayTooltip>
                  </div>
                </div>
              )}
              <div className="relative w-full">
                <div className="w-full whitespace-pre-wrap pr-8 font-mono text-sm leading-relaxed text-gray-700">
                  {formatCode(code)}
                </div>
                <div className="absolute right-0 top-0 z-10">
                  <CopyButton
                    text={code}
                    className="bg-white/70 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    title="Copy code"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default CodeAccordion;
